import React, { useCallback, useState, useRef } from 'react';

import useFiles from '@hooks/useFiles';
import { colorSet, useSnackbar, clsx } from '@components/Provider';

import {
  CustomizedTypography,
  CustomizedCircularProgress
} from '@components/Customized';

import { Box, makeStyles, Theme } from '@material-ui/core';

import axios, { Canceler } from 'axios';
import { getExtension } from '@components/Chat/ChatMenu/Viewer';
import openViewer from '@utils/popupViewer';

interface RefFile {
  fileName?: string | null;
  fileId?: string | null;
  fileSize?: number | null;
}

interface Props {
  files?: RefFile[];
  rfqId?: string;
}

const ReferenceFileList = ({ files, rfqId }: Props) => {
  const classes = useStyles({});

  const convertedFiles =
    files && files.length > 0
      ? files.map((file) => ({
          fileName: file?.fileName ?? '',
          fileId: file?.fileId ?? '',
          fileSize: file.fileSize ?? 0,
          fullName: file?.fileName ?? '',
          fileExtension: getExtension(file?.fileName ?? '')
        }))
      : [];

  return (
    <div className={classes.referenceFileContainer}>
      {convertedFiles?.map((file, index) => (
        <ReferenceFile
          key={index}
          {...file}
          gutterBottom={
            convertedFiles.length > 2 && convertedFiles.length - 1 > index
          }
          gutterRight={index % 2 === 0}
          rfqId={rfqId}
        />
      ))}
    </div>
  );
};

interface ReferenceFileProps {
  gutterBottom: boolean;
  gutterRight: boolean;
  fileName: string;
  fullName: string;
  fileId: string;
  fileSize: number;
  fileExtension: string;
  rfqId?: string;
}

const ReferenceFile = ({
  gutterBottom,
  gutterRight,
  fileName,
  fullName,
  fileId,
  fileSize,
  fileExtension,
  rfqId
}: ReferenceFileProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles({});

  const source = useRef<Canceler | null>(null);

  const [start, setStart] = useState(false);

  const [value, setValue] = useState(0);

  const { onDownloadFile } = useFiles();

  const onCancel = useCallback(() => {
    if (source.current) {
      source.current();
    }

    setValue(0);
    setStart(false);
  }, []);

  const onDownload = useCallback(async () => {
    setStart(true);
    const CancelToken = axios.CancelToken;

    if (start) return;

    try {
      await onDownloadFile(
        { fileId, fileName: fullName },
        {
          cancelToken: new CancelToken(function executor(c) {
            source.current = c;
          }),
          onDownloadProgress: (e) => {
            if (fileSize) {
              const percent = Math.round((e.loaded * 100) / fileSize);

              setValue(percent);
            }
          }
        }
      );
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }
      enqueueSnackbar(`파일 다운로드에 실패했습니다.`, {
        variant: 'error'
      });
    }

    setStart(false);
    setValue(0);

    // eslint-disable-next-line
  }, [fileId, fullName, fileSize, start]);

  const withoutExtentionFileName = fileName.slice(
    0,
    fileName.lastIndexOf('.') + 1
  );

  return (
    <div
      className={clsx(classes.referenceFile, {
        'mb-16': gutterBottom,
        'mr-16': gutterRight
      })}
    >
      <div className="top">
        <Box width="calc(100% - 40px)" display="flex">
          <CustomizedTypography
            className="fileName"
            variant="body1"
            color="textPrimary"
            fontWeight="500"
            noWrap
          >
            {withoutExtentionFileName}
          </CustomizedTypography>
          <CustomizedTypography
            className="file-extention"
            variant="body1"
            color="textPrimary"
            fontWeight="500"
          >
            {fileExtension}
          </CustomizedTypography>
        </Box>

        <CustomizedCircularProgress
          classes={{ root: 'circularProgress' }}
          variant="download"
          color="primary"
          onDownload={() => {
            onDownload();
          }}
          value={value}
        />
      </div>
      <div className="bottom">
        <CustomizedTypography
          className="clickable"
          variant="body2"
          color="textPrimary"
          fontWeight="500"
          onClick={(e) => {
            e.stopPropagation();
            openViewer({
              rfqId: rfqId
            });
          }}
        >
          뷰어로 보기
        </CustomizedTypography>

        <CustomizedTypography
          className="dot"
          variant="caption"
          color="textSecondary"
          fontWeight="500"
        >
          ・
        </CustomizedTypography>

        {!start ? (
          <CustomizedTypography
            className="clickable"
            variant="body2"
            color="primary"
            fontWeight="500"
            onClick={(e) => {
              e.stopPropagation();

              onDownload();
            }}
          >
            다운로드
          </CustomizedTypography>
        ) : (
          <CustomizedTypography
            className="clickable"
            variant="body2"
            color="error"
            fontWeight="500"
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
            }}
          >
            취소
          </CustomizedTypography>
        )}
      </div>
    </div>
  );
};

export default ReferenceFileList;

const useStyles = makeStyles<Theme, { matches?: boolean }>((theme) => {
  return {
    referenceFileContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      width: '100%'
    },
    referenceFile: {
      maxWidth: 'calc(100% / 2 - 8px)',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      padding: '12px 14px',
      backgroundColor: colorSet.gray100,
      borderRadius: 8,
      transition: 'background-color 0.1s ease-in-out',
      [theme.breakpoints.down('md')]: {
        maxWidth: '100%'
      },
      '&:hover': {
        backgroundColor: colorSet.gray300
      },
      '&.mb-16': {
        marginBottom: 16,
        [theme.breakpoints.down('md')]: {
          marginBottom: 8
        }
      },
      '&.mr-16': {
        marginRight: 16,
        [theme.breakpoints.down('md')]: {
          marginRight: 0
        }
      },
      '& .top': {
        position: 'relative',
        display: 'flex',
        marginBottom: 8,
        '& .circularProgress': {
          marginLeft: 'auto'
        }
      },
      '& .bottom': {
        display: 'flex',
        alignItems: 'center',
        '& .dot': {
          margin: '0px 4px'
        },
        '& .clickable': {
          cursor: 'pointer'
        },
        '&.image': {
          padding: 16,
          backgroundColor: colorSet.gray000,
          borderRadius: '0px 0px 10px 10px'
        }
      }
    }
  };
});
